import { supabase } from './supabase';
import type { MetaAlignmentFilters } from '../../shared/types/metaAlignment';

export type MetaInsightsAggregate = {
  object_id: string;
  sum_spend: number | null;
  sum_impressions: number | null;
  sum_clicks: number | null;
  sum_conversions: number | null;
  sum_conversion_value: number | null;
};

const sanitizeIds = (ids: string[]) =>
  Array.from(new Set(ids.filter((id) => typeof id === 'string' && id.trim().length > 0)));

const accumulateMetrics = (
  map: Map<string, MetaInsightsAggregate>,
  rows: Array<{
    object_id: string;
    spend?: number | string | null;
    impressions?: number | string | null;
    clicks?: number | string | null;
    conversions?: number | string | null;
    conversion_value?: number | string | null;
  }>
) => {
  rows.forEach((row) => {
    const objectId = row.object_id;
    if (!objectId) return;

    const existing = map.get(objectId) ?? {
      object_id: objectId,
      sum_spend: 0,
      sum_impressions: 0,
      sum_clicks: 0,
      sum_conversions: 0,
      sum_conversion_value: 0,
    };

    existing.sum_spend = Number(existing.sum_spend || 0) + Number(row.spend || 0);
    existing.sum_impressions = Number(existing.sum_impressions || 0) + Number(row.impressions || 0);
    existing.sum_clicks = Number(existing.sum_clicks || 0) + Number(row.clicks || 0);
    existing.sum_conversions = Number(existing.sum_conversions || 0) + Number(row.conversions || 0);
    existing.sum_conversion_value = Number(existing.sum_conversion_value || 0) + Number(row.conversion_value || 0);

    map.set(objectId, existing);
  });
};

export const fetchMetaInsightMetrics = async (
  objectType: 'campaign' | 'adset' | 'ad',
  objectIds: string[],
  filters: Pick<MetaAlignmentFilters, 'dateFrom' | 'dateTo'>
): Promise<Map<string, MetaInsightsAggregate>> => {
  const map = new Map<string, MetaInsightsAggregate>();
  const ids = sanitizeIds(objectIds);
  if (!ids.length) return map;

  const chunkSize = 50;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    if (!chunk.length) continue;

    let query = supabase
      .from('meta_insights')
      .select('object_id, spend, impressions, clicks, conversions, conversion_value')
      .eq('object_type', objectType)
      .in('object_id', chunk);

    if (filters.dateFrom) {
      query = query.gte('date_start', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('date_stop', filters.dateTo);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Failed to load meta insight metrics:', { objectType, error });
      continue;
    }

    accumulateMetrics(map, (data ?? []) as any[]);
  }

  return map;
};

export const normalizeMetricsRow = (row?: MetaInsightsAggregate | null) => {
  const spend = Number(row?.sum_spend || 0);
  const impressions = Number(row?.sum_impressions || 0);
  const clicks = Number(row?.sum_clicks || 0);
  const conversions = Number(row?.sum_conversions || 0);
  const conversionValue = Number(row?.sum_conversion_value || 0);
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;

  return {
    spend,
    impressions,
    clicks,
    ctr,
    conversions,
    conversionValue,
  };
};

export type MetaInsightMetrics = ReturnType<typeof normalizeMetricsRow>;
